import React, { useState } from "react";
import styled from "styled-components";
import { SVGS } from "../../assets/svgs.js";
import { Button, CancelButton } from "../button/button.jsx";
import { ContentsContainer } from "../card/cardcontainer.jsx";
import { IconWrapper } from "../iconWrapper/wrapper.jsx";
import { InputText } from "../inputs/inputs.jsx";
import { BoldText } from "../normalText/normalText.jsx";
import styles from "./bank.module.css";
import config from "../../config.js";
import axios from "axios";
import * as yup from "yup";
import { WqToast as toast } from "../../utils/themedToast";
import { hideLoader, showLoader } from "../../utils/loader.jsx";
import {
  getEmbedContext,
} from "../../utils/embed.js";
import { useSearchParams } from "react-router-dom";
import { finalizePaymentSuccess, showPaymentError } from "../../utils/paymentOutcomes.js";

const PWW_BASE = `${config.API_ROOT_ADDRESS}/api/v1/pay-with-waya`;

/* Yup schemas. .test drives the digit-format checks. */
const initiateSchema = yup.object({
  sourceAccountNumber: yup
    .string()
    .trim()
    .required("Account number is required")
    .test(
      "acct",
      "Enter a valid 10-digit account number",
      (v) => /^\d{10}$/.test(v || "")
    ),
  pin: yup
    .string()
    .trim()
    .required("PIN is required")
    .test("pin", "PIN must be 4 digits", (v) => /^\d{4}$/.test(v || "")),
  narration: yup
    .string()
    .trim()
    .required("Narration is required")
    .max(100, "Narration is too long"),
});

const otpSchema = yup.object({
  otp: yup
    .string()
    .trim()
    .required("OTP is required")
    .test("otp", "Enter the OTP sent to your phone", (v) =>
      /^\d{4,6}$/.test(v || "")
    ),
});

const collectErrors = (err) => {
  const out = {};
  (err.inner || []).forEach((e) => {
    if (e.path && !out[e.path]) out[e.path] = e.message;
  });
  return out;
};

const { bankIcon, Ussd } = SVGS;

const FieldError = ({ message }) =>
  message ? (
    <p className="flex mt-2" style={{ paddingLeft: 4, fontSize: 14 }}>
      <span>
        <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.66634 8.125H7.33301V4.375H8.66634V8.125ZM8.66634 10.625H7.33301V9.375H8.66634V10.625ZM7.99967 1.25C7.1242 1.25 6.25729 1.41166 5.44845 1.72575C4.63961 2.03984 3.90469 2.50022 3.28563 3.08058C2.03539 4.25269 1.33301 5.8424 1.33301 7.5C1.33301 9.1576 2.03539 10.7473 3.28563 11.9194C3.90469 12.4998 4.63961 12.9602 5.44845 13.2742C6.25729 13.5883 7.1242 13.75 7.99967 13.75C9.76779 13.75 11.4635 13.0915 12.7137 11.9194C13.964 10.7473 14.6663 9.1576 14.6663 7.5C14.6663 6.67924 14.4939 5.86651 14.1589 5.10823C13.8238 4.34994 13.3328 3.66095 12.7137 3.08058C12.0947 2.50022 11.3597 2.03984 10.5509 1.72575C9.74206 1.41166 8.87515 1.25 7.99967 1.25Z" fill="#FF6700" />
        </svg>
      </span>
      <span className="text-primary-theme ml-2 text-xs">{message}</span>
    </p>
  ) : null;

export const Bank = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [sourceAccountNumber, setSourceAccountNumber] = useState("");
  const [pin, setPin] = useState("");
  const [narration, setNarration] = useState("");
  const [otp, setOtp] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { isEmbedded } = getEmbedContext();
  // refNo is the transaction id (storage first, URL fallback); wayaPublicKey
  // is published into storage by the container when the transaction is loaded.
  const refNo =
    sessionStorage.getItem("tranId") ||
    new URLSearchParams(window.location.search).get("_tranId");
  const wayaPublicKey = sessionStorage.getItem("PUBLIC_KEY") || searchParams.get('publicKey');

  const onDigits = (setter, max) => (e) => {
    const next = e.target.value.replace(/\D/g, "").slice(0, max);
    setter(next);
  };


  const initiate = async () => {
    try {
      await initiateSchema.validate(
        { sourceAccountNumber, pin, narration },
        { abortEarly: false }
      );
      setErrors({});
    } catch (e) {
      setErrors(collectErrors(e));
      return;
    }

    if (!refNo || !wayaPublicKey) {
      showPaymentError(null, "Missing transaction reference. Please reopen the payment.");
      return;
    }

    setLoading(true);
    showLoader();
    try {
      const { data } = await axios.post(`${PWW_BASE}/initiate`, {
        narration,
        pin,
        refNo,
        sourceAccountNumber,
        wayaPublicKey,
      });

      if (data?.status === false) {
        // showError(null, data?.message);
        showPaymentError(null, data?.message);
        return;
      }

      toast.success(data?.message ?? "OTP sent to your phone");
      setOtp("");
      setStep(2);
    } catch (err) {
      showPaymentError(null, "Could not initiate the payment");
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const verify = async () => {
    try {
      await otpSchema.validate({ otp }, { abortEarly: false });
      setErrors({});
    } catch (e) {
      setErrors(collectErrors(e));
      return;
    }

    setLoading(true);
    showLoader();
    try {
      const { data } = await axios.post(`${PWW_BASE}/verify`, {
        otp,
        refNo,
        wayaPublicKey,
      });

      if (data?.status === false) {
        showPaymentError(null, data?.message);
        return;
      }

      finalizePaymentSuccess({ message: data?.message, refNo, isEmbedded });
    } catch (err) {
      showPaymentError(err, "Could not verify the OTP");
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const resendOtp = async () => {
    if (!refNo || !wayaPublicKey) return;
    setResending(true);
    try {
      const { data } = await axios.post(`${PWW_BASE}/resend-otp`, {
        refNo,
        wayaPublicKey,
      });
      toast.success(data?.message ?? "A new OTP has been sent");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Could not resend the OTP"
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <ContentsContainer>
      <div className={styles.cardcontainer}>
      {step === 1 && (
        <div className={styles.contentsBetween}>
          <TextPrompt className={styles.text}>
            <div style={{ marginBottom: 10 }}>
              <IconWrapper pad={20}>
                <img src={bankIcon} alt="" />
              </IconWrapper>
            </div>
            <BoldText align="center" size={14}>
              Pay with your WayaBank account. Enter details below.
            </BoldText>
          </TextPrompt>

          <div className={styles.inputContainer2}>
            <InputText
              placeholder="Source Account Number (10 digits)"
              value={sourceAccountNumber}
              onChange={onDigits(setSourceAccountNumber, 10)}
              type="text"
              pattern="\d{10}"
            />
            <FieldError message={errors.sourceAccountNumber} />

            <div style={{ marginTop: 14 }}>
              <InputText
                placeholder="Transaction PIN (4 digits)"
                value={pin}
                onChange={onDigits(setPin, 4)}
                type="password"
                pattern="\d{4}"
              />
              <FieldError message={errors.pin} />
            </div>

            <div style={{ marginTop: 14 }}>
              <InputText
                placeholder="Narration"
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
                type="text"
              />
              <FieldError message={errors.narration} />
            </div>
          </div>

          <div className={styles.buttonContainer2}>
            <Button
              type={"button"}
              text="Pay Now"
              active={true}
              loading={loading}
              onClick={initiate}
              disabled={!sourceAccountNumber || !pin || !narration}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className={styles.contentsBetween}>
          <div className={styles.text}>
            <div style={{ marginBottom: 10 }}>
              <IconWrapper pad={20}>
                <img src={Ussd} alt="" />
              </IconWrapper>
            </div>
            <BoldText align="center" size={14}>
              Enter the OTP sent to the phone registered with your WayaBank
              account
            </BoldText>
          </div>

          <div className={styles.inputContainer4}>
            <InputText
              placeholder="Enter OTP here"
              value={otp}
              onChange={onDigits(setOtp, 6)}
              type="number"
              pattern="\d{6}"
            />
            <FieldError message={errors.otp} />

            <div style={{ marginTop: 10, textAlign: "center" }}>
              <button
                type="button"
                onClick={resendOtp}
                disabled={resending}
                  // className="text-primary-theme text-sm"
                  className={styles.resendButton}
                // style={{ background: "none", border: "none", cursor: "pointer" }}
              >
                {resending ? "Resending..." : "Resend OTP"}
              </button>
            </div>
          </div>

          <div className={styles.buttonContainer2}>
            <Button
              type={"button"}
              text="Authorize Payment"
              active={true}
              loading={loading}
              onClick={verify}
              disabled={!otp}
            />
            <CancelButton text="cancel" onClick={() => setStep(1)} />
          </div>
        </div>
        )}
          </div>
    </ContentsContainer>
  
  );
};


const TextPrompt = styled.div`
  
  // border: 1px solid red;
   padding: 10px; 
  display: flex;
  border-radius: 8px;
  justify-content: center;
  text-align: center;
  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
  .text-prompt {
    color: black;
    font-weight: 700;
  }
`;